// BUILD-042 cycle 12 - Employee details: the pure rules (no DOM, no React, no network). Tested under plain Node (detailsModel.test.mjs).
//
// 1. IDENTITY. Name and Work email are read-only facts that come from the authorized identity source (the HR roster/directory entry for the person).
//    When that source does not carry a value, the screen says "Not available". Nothing is guessed, and the mock may not paper over a missing mapping.
// 2. TOUCHED FIELDS. A save sends ONLY the fields the person actually edited. The form is a snapshot taken when the step opened; if the server's view of the
//    record has since moved (a reload after a partial save, another HR user's change), a field the person never touched adopts the server's value instead of
//    being compared against it - so an untouched field can never be "found different" and written back as a stale blank.

export const NOT_AVAILABLE = 'Not available';

export interface DirectoryEntryLike { employeeId: string; name?: string | null; email?: string | null }
export interface PersonLike { name: string; email: string | null }
/** Placeholders the screens use while a name has not loaded yet; they are not names. */
const PLACEHOLDER_NAMES = new Set(['This person', 'Employee']);

const clean = (s: string | null | undefined): string | null => {
  const t = (s ?? '').trim();
  return t ? t : null;
};

/** The name and work email to SHOW. The directory entry (authorized identity source) wins; a value that is absent everywhere is null ("Not available"). */
export function resolveIdentity(person: PersonLike | null | undefined, entry: DirectoryEntryLike | null | undefined): { name: string | null; email: string | null } {
  const personName = clean(person?.name);
  return {
    name: clean(entry?.name) ?? (personName && !PLACEHOLDER_NAMES.has(personName) ? personName : null),
    email: clean(entry?.email) ?? clean(person?.email),
  };
}

// ─── Touched-field tracking for the three job fields + the phone ──────────────────────────────────────────────────────────────────
export type DetailsField = 'title' | 'departmentId' | 'managerEmployeeId' | 'phone';
export type Touched = Partial<Record<DetailsField, true>>;
export interface ValuesLike { title: string; departmentId: string; managerEmployeeId: string; phone: string }
export interface BaselineLike { title: string | null; departmentId: string | null; managerEmployeeId: string | null; phone: string | null }

export const baselineValues = (b: BaselineLike): ValuesLike => ({ title: b.title ?? '', departmentId: b.departmentId ?? '', managerEmployeeId: b.managerEmployeeId ?? '', phone: b.phone ?? '' });

/** Mark a field as edited. */
export function touch(touched: Touched, ...fields: DetailsField[]): Touched {
  const next = { ...touched };
  for (const f of fields) next[f] = true;
  return next;
}
/** Fields whose value differs from the previous form values (what the person just edited). */
export function editedFields(prev: ValuesLike, next: ValuesLike): DetailsField[] {
  return (['title', 'departmentId', 'managerEmployeeId', 'phone'] as DetailsField[]).filter((f) => prev[f] !== next[f]);
}
/** The values a save should compare/send: untouched fields ARE the server's current values (so they can never differ), touched fields are what the person entered. */
export function valuesForSave(baseline: BaselineLike, values: ValuesLike, touched: Touched): ValuesLike {
  const b = baselineValues(baseline);
  return {
    title: touched.title ? values.title : b.title,
    departmentId: touched.departmentId ? values.departmentId : b.departmentId,
    managerEmployeeId: touched.managerEmployeeId ? values.managerEmployeeId : b.managerEmployeeId,
    phone: touched.phone ? values.phone : b.phone,
  };
}
/** The form values to SHOW after the server's view changed: untouched fields follow the server, touched fields keep the person's entry. */
export function adoptBaseline(values: ValuesLike, touched: Touched, baseline: BaselineLike): ValuesLike {
  return valuesForSave(baseline, values, touched);
}
/** After a successful save (baseline now equals what was entered) a field is no longer "edited". */
export function pruneTouched(touched: Touched, values: ValuesLike, baseline: BaselineLike): Touched {
  const b = baselineValues(baseline);
  const next: Touched = {};
  for (const f of Object.keys(touched) as DetailsField[]) {
    const v = f === 'title' || f === 'phone' ? values[f].trim() !== b[f].trim() : values[f] !== b[f];
    if (v) next[f] = true;
  }
  return next;
}
